import {
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import React, { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { TextField } from '@ofqual/portal-components/components/TextField';
import { DatePicker } from '@ofqual/portal-components/components/DatePicker';
import { SectionProps } from './SectionProps';
import { fieldLabels } from './fieldLabels';
import { Conditions } from './Conditions';
import { SocItemDetailsAo } from '../../domain/SocItemDetailsAo';
import { Error } from "../common/Error";


export const CurrentCompliance: React.FC<SectionProps<SocItemDetailsAo>> = (
  sectionProps,
) => {
  const { control, disabled, errors, formState: { isSubmitted }, trigger } = sectionProps;

  const { currentCompliant } = useWatch<SocItemDetailsAo>({
    control,
    name: ['currentCompliant'],
  });

  useEffect(() => {
    if (isSubmitted) {
      trigger()
    }
  }, [trigger, currentCompliant, isSubmitted]);


  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="currentCompliant"
          rules={{ validate: (value) => value !== null }}
          render={(props) => (
            <FormControl component="fieldset" required disabled={disabled}>
              <FormLabel
                id="currentCompliant-label"
                component="legend"
                style={{ marginBottom: '10px' }}
              >
                {fieldLabels.currentCompliant}
              </FormLabel>
              <RadioGroup
                aria-labelledby="currentCompliant-label"
                name="currentCompliant"
                data-test="radiogroup-currentCompliant"
                onChange={(e) => props.onChange(e.target.value === 'true')}
              >
                <FormControlLabel
                  value={true}
                  label={fieldLabels.currentCompliantYes}
                  control={<Radio />}
                  data-test="currentCompliantYes"
                  checked={props.value === true}
                  style={{ marginBottom: '10px' }}
                />
                <FormControlLabel
                  value={false}
                  label={fieldLabels.currentCompliantNo}
                  control={<Radio />}
                  data-test="currentCompliantNo"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        {errors.currentCompliant && (
          <>
            <br></br>
            <Error data-test="currentCompliant-validation">
              You must select at least one option
            </Error>
          </>
        )}

      </Grid>
      {currentCompliant === false ? (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{ required: true}}
              name="currentNonComplianceDescription"
              render={(props) => (
                <>
                  <TextField
                    {...props}
                    label={fieldLabels.currentNonComplianceDescription}
                    required
                    data-test="currentNonComplianceDescription-field"
                    multiline
                    rows={8}
                    readOnly={disabled}
                  />
                </>
              )}
            />
            {errors.currentNonComplianceDescription?.type === "required" && (
              <Error data-test="currentNonComplianceDescription-validation">
                This field is required
              </Error>
            )}
          </Grid>

          <Conditions type="Current" {...sectionProps} />
          <Grid item xs={12}>
            <Controller
              control={control}
              name="currentNonComplianceNonOfqualConditions"
              rules={{ required: true }}
              render={(props) => (
                <>
                  <TextField
                    {...props}
                    label={fieldLabels.nonComplianceNonOfqualConditions}
                    data-test="currentNonComplianceNonOfqualConditions-field"
                    multiline
                    rows={8}
                    readOnly={disabled}
                    required
                  />
                </>
              )}
            />
            {errors.currentNonComplianceNonOfqualConditions?.type === "required" && (
              <Error data-test="currentNonComplianceNonOfqualConditions-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="currentNonComplianceMitigation"
              rules={{ required: true }}
              render={(props) => (
                <>
                  <TextField
                    {...props}
                    label={fieldLabels.currentNonComplianceMitigation}
                    required
                    data-test="currentNonComplianceMitigation-field"
                    multiline
                    rows={8}
                    readOnly={disabled}
                  />
                </>
              )}
            />
            {errors.currentNonComplianceMitigation?.type === "required" && (
              <Error data-test="currentNonComplianceMitigation-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="currentCompliantDate"
              control={control}
              rules={{ required: true}}
              as={
                <DatePicker
                  dataTest="currentCompliantDate-picker"
                  label={fieldLabels.currentCompliantDate}
                  readOnly={disabled}
                  required
                  clearable
                />
              }
            />
            {errors.currentCompliantDate?.type === "required" && (
              <Error data-test="currentCompliantDate-validation">
                A date is required
              </Error>
            )}
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};
